import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import { useTextMonitoring } from "@/hooks/supervisor";
import {
	CustomAvatarOptions,
	ImageOverlay,
	InlineImage,
	MessageThread,
} from "@azure/communication-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChatStyle } from "./style";
import { MessagingInteractionContentProps, MessagingMethods } from "./types";
import useOnRenderAvatar from "./Avatar";
import { useResponsiveBreakpoints } from "@/hooks/responsive";

export type ImageWrapperFn = (
	image: JSX.Element,
	imageId: string,
	src: string
) => JSX.Element;

interface OverlayImageItem {
	imageSrc: string;
	downloadImageName: string;
}

interface MessagingChatLayoutProps {
	messagingMethods: MessagingMethods;
	props: MessagingInteractionContentProps;
	sendBoxContent?: JSX.Element;
	overlayImageItem?: OverlayImageItem;
	onOverlayDismiss?: () => void;
	containerRef?: (el: HTMLDivElement | null) => void;
}

export const useImageOverlay = () => {
	const [overlayImageItem, setOverlayImageItem] = useState<OverlayImageItem>();

	const onInlineImageClicked = useCallback(
		(attachmentId: string, imgSource: string): Promise<void> => {
			setOverlayImageItem({
				downloadImageName: attachmentId,
				imageSrc: imgSource,
			});
			return Promise.resolve();
		},
		[]
	);

	const imageWrapper: ImageWrapperFn = useCallback(
		(image: JSX.Element, imageId: string, src: string) => {
			return (
				<span
					onClick={() => onInlineImageClicked(imageId, src)}
					tabIndex={0}
					role="button"
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							onInlineImageClicked(imageId, src);
						}
					}}
				>
					{image}
				</span>
			);
		},
		[onInlineImageClicked]
	);

	const dismissOverlay = useCallback(() => {
		setOverlayImageItem(undefined);
	}, []);

	return { overlayImageItem, dismissOverlay, imageWrapper };
};

export const MessagingChatLayout = ({
	messagingMethods,
	props,
	sendBoxContent,
	overlayImageItem: externalOverlayItem,
	onOverlayDismiss: externalOverlayDismiss,
	containerRef: externalContainerRef,
}: MessagingChatLayoutProps) => {
	const { hideSendBox = false, isVariableTextOpen } = props;
	const classes = useChatStyle();
	const onRenderAvatar = useOnRenderAvatar();
	const isTextMonitoring = useTextMonitoring();
	const { isMobileMediumToSmall } = useResponsiveBreakpoints();

	const containerRef = useRef<HTMLDivElement | null>(null);
	const editorRef = useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [threadHeight, setThreadHeight] = useState<number | undefined>(
		undefined
	);

	const {
		overlayImageItem: internalOverlayItem,
		dismissOverlay: internalDismiss,
		imageWrapper,
	} = useImageOverlay();

	const overlayImageItem = externalOverlayItem ?? internalOverlayItem;
	const dismissOverlay = externalOverlayDismiss ?? internalDismiss;

	const inlineImageOptions = {
		onRenderInlineImage: (
			inlineImage: InlineImage,
			defaultOnRender: (inlineImage: InlineImage) => JSX.Element
		): JSX.Element => {
			return imageWrapper(
				defaultOnRender(inlineImage),
				inlineImage.imageAttributes.id || "",
				inlineImage.imageAttributes.src || ""
			);
		},
	};

	const updateHeights = () => {
		if (hideSendBox) {
			if (containerRef.current) {
				setThreadHeight(containerRef.current.clientHeight);
			}
			return;
		}
		if (containerRef.current && editorRef.current) {
			const containerHeight = containerRef.current.clientHeight;
			const editorHeight = editorRef.current.offsetHeight;
			setThreadHeight(containerHeight - editorHeight);
		}
	};

	useEffect(() => {
		const observer = new ResizeObserver(() => {
			updateHeights();
		});

		// Always observe the conversation container, not just the composer. The container is
		// flex-sized to fill the workspace below the CRM picker and variable-text bars, so
		// collapsing the CRM (Salesforce/Dynamics) section resizes the container WITHOUT resizing
		// the composer. Observing only the composer (the previous behavior) left threadHeight stale
		// on collapse: the message thread kept its old height and the send box floated up the panel
		// with dead space beneath it. When the send box is shown, also observe it so a growing
		// multi-line composer still reclaims thread height.
		if (containerRef.current) {
			observer.observe(containerRef.current);
		}
		if (!hideSendBox && editorRef.current) {
			observer.observe(editorRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, [hideSendBox, isTextMonitoring]);

	useEffect(() => {
		updateHeights();
	}, [isVariableTextOpen]);

	useEffect(() => {
		if (messagingMethods.chatMessages.length > 0) {
			const latestMessage =
				messagingMethods.chatMessages[messagingMethods.chatMessages.length - 1];

			const hasImage = latestMessage?.content?.includes("<img");

			if (hasImage) {
				if (scrollContainerRef.current) {
					const scrollableElement = Array.from(
						scrollContainerRef.current.querySelectorAll("*")
					).find((el: any) => {
						return el.scrollHeight > el.clientHeight;
					}) as HTMLElement;

					if (scrollableElement) {
						const isNearBottom =
							scrollableElement.scrollTop + scrollableElement.clientHeight >=
							scrollableElement.scrollHeight - 100;

						if (isNearBottom) {
							setTimeout(() => {
								scrollableElement.scrollTop = scrollableElement.scrollHeight;
							}, 1000);
						}
					} else {
						const container = scrollContainerRef.current;
						if (container.scrollHeight > container.clientHeight) {
							const isNearBottom =
								container.scrollTop + container.clientHeight >=
								container.scrollHeight - 100;
							if (isNearBottom) {
								setTimeout(() => {
									container.scrollTop = container.scrollHeight;
								}, 1000);
							}
						}
					}
				}
			}
		}
	}, [messagingMethods.chatMessages]);

	const onCustomRenderAvatar = (
		userId?: string,
		options?: CustomAvatarOptions
	) => {
		return isMobileMediumToSmall ? <></> : onRenderAvatar(userId, options);
	};

	const messagingChatThread = useMemo(() => {
		if (messagingMethods.chatMessages.length === 0) {
			return <></>;
		}

		return (
			<MessageThread
				userId={Lockr.get(StorageKeys.UserId)}
				messages={messagingMethods.chatMessages}
				showMessageDate
				disableEditing
				onRenderAttachmentDownloads={messagingMethods.onRenderAttachments}
				onRenderMessage={messagingMethods.onRenderMessage}
				onRenderAvatar={onCustomRenderAvatar}
				inlineImageOptions={inlineImageOptions}
				disableJumpToNewMessageButton
				styles={{
					chatContainer: {
						paddingLeft: "calc((50% - 200px) / 2)",
						paddingRight: "calc((50% - 200px) / 2)",
						paddingBottom: "20px",
					},
				}}
				strings={{ failToSendTag: "" }}
			/>
		);
	}, [messagingMethods.chatMessages, onRenderAvatar, inlineImageOptions]);

	return (
		<div
			className={classes.conversation}
			ref={(el) => {
				containerRef.current = el;
				externalContainerRef?.(el);
			}}
		>
			<div
				ref={scrollContainerRef}
				style={{
					height: threadHeight ?? "auto",
					overflow: "auto",
				}}
			>
				{messagingChatThread}
			</div>
			{
				<ImageOverlay
					isOpen={overlayImageItem !== undefined}
					imageSrc={overlayImageItem?.imageSrc || ""}
					title="Image"
					onDismiss={dismissOverlay}
					onDownloadButtonClicked={() => {
						const link = document.createElement("a");
						link.href = overlayImageItem?.imageSrc || "";
						link.download = overlayImageItem?.downloadImageName || "";
						link.click();
					}}
				/>
			}
			{!hideSendBox && !isTextMonitoring && (
				<div className={classes.textEditor} ref={editorRef}>
					{sendBoxContent}
				</div>
			)}
		</div>
	);
};
